import React, { useState, useEffect, useRef } from "react";
import { Table, Tag, Space, Tooltip, ConfigProvider, Badge } from "antd";
import { PictureOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUld } from "../context/UldContext";
import { useTableSelection } from "../../../../hooks";
import UldCoreStatusTag from "./UldCoreStatusTag";
import UldAvailabilityTag from "./UldAvailabilityTag";
import UldActionDropdownV3 from "./UldActionDropdownV3";
import UldPhotoModal from "./UldPhotoModal";
import { ULD_LOCATION_TYPE_NAMES } from "../constants/uldConstants";

const UldTable = ({ onSelectionChange }) => {
    // 添加导航hook
    const navigate = useNavigate();
    
    const { uldList, loading, totalUlds, page, pageSize, updatePagination, updateSortInfo, sortInfo, fetchUlds, showEditForm } = useUld();

    const [photoModalVisible, setPhotoModalVisible] = useState(false);
    const [photoRecord, setPhotoRecord] = useState(null);

    // 使用航班监控页面的多选交互模式
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getIndexColumn, getRowProps, clearSelection, handleRowSelectionChange } = useTableSelection({
        getRowKey: (record) => record.master.id,
        dataSource: uldList || [],
        pagination: { current: page, pageSize: pageSize },
        mouseMoveThrottle: 120, // 提高节流时间，减少处理频率
    });

    // 监听选择状态变化，通过props回调通知父组件
    React.useEffect(() => {
        if (onSelectionChange) {
            // 提取选中行的ID数组
            const selectedUldIds = selectedRows.map(record => record.master.id);
            onSelectionChange(selectedUldIds, selectedRows, selectedRowKeys, clearSelection, handleRowSelectionChange);
        }
    }, [selectedRows, selectedRowKeys, onSelectionChange, clearSelection, handleRowSelectionChange]);

    // 自定义行属性函数 - 只允许序号列选中
    const getCustomRowProps = React.useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);

            // 移除原始的 onClick 事件，只保留其他属性
            const { onClick, ...restProps } = originalProps;

            return {
                ...restProps,
                // 不添加 onClick 事件，这样点击行的其他部分不会触发选中
            };
        },
        [getRowProps],
    );

    // 优化的表格列定义 - 调整宽度和布局
    const columns = [
        getIndexColumn("序号", 50, { fixed: "left" }),
        {
            title: "ULD识别码",
            dataIndex: ["master", "uld_identifier"],
            key: "uld_identifier",
            width: 110,
            fixed: 'left',
            ellipsis: {
                showTitle: false,
            },
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    <span style={{ fontWeight: 500, color: '#1890ff' }}>{text}</span>
                </Tooltip>
            ),
        },
        {
            title: "类型",
            dataIndex: ["master", "uld_type"],
            key: "uld_type",
            width: 70,
            render: (type) => (
                <Tag color="blue" style={{ fontSize: '11px', padding: '2px 6px', margin: 0 }}>
                    {type}
                </Tag>
            ),
        },
        {
            title: "核心状态",
            dataIndex: ["stock", "core_status"],
            key: "core_status",
            width: 80,
            render: (coreStatus, record) => (
                <UldCoreStatusTag 
                    status={coreStatus} 
                    description={`当前位置: ${record.stock?.current_location_type || '未知'}`}
                    size="small"
                />
            ),
        },
        {
            title: "可用性",
            dataIndex: ["stock", "availability"],
            key: "availability",
            width: 80,
            render: (availability) => (
                <UldAvailabilityTag availability={availability} size="small" />
            ),
        },
        {
            title: "外放状态",
            key: "delivery_status",
            width: 100,
            render: (_, record) => {
                const isDelivered = record.master?.delivered_to_consignee;
                const consigneeName = record.master?.consignee_name;
                
                if (isDelivered) {
                    return (
                        <Tooltip title={`收货人: ${consigneeName || '未知'}`}>
                            <Tag color="orange" size="small">已外放</Tag>
                        </Tooltip>
                    );
                }
                return <Tag color="green" size="small">未外放</Tag>;
            },
        },
        {
            title: "当前位置",
            key: "current_location",
            width: 160,
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => {
                const { stock } = record;
                if (!stock) return <Tag size="small">未知</Tag>;

                const locationType = stock.current_location_type;
                const locationName = ULD_LOCATION_TYPE_NAMES[locationType] || locationType;

                let locationDetail = "";
                if (locationType === "AIRPORT" && stock.current_airport_iata) {
                    locationDetail = stock.current_airport_iata;
                } else if (locationType === "FLIGHT") {
                    if (record.current_flight_number) {
                        locationDetail = record.current_flight_number;
                    } else if (stock.current_flight_op_id) {
                        locationDetail = `航班ID: ${stock.current_flight_op_id}`;
                    } else if (stock.transport_identifier) {
                        locationDetail = stock.transport_identifier;
                    }
                } else if (locationType === "OTHER_LOCATION" && stock.current_other_location) {
                    locationDetail = stock.current_other_location;
                }

                const displayText = locationDetail ? `${locationName} ${locationDetail}` : locationName;
                return (
                    <Tooltip placement="topLeft" title={displayText}>
                        <Space size={4}>
                            <Tag size="small" color="geekblue">{locationName}</Tag>
                            {locationDetail && <span style={{ fontSize: '12px' }}>{locationDetail}</span>}
                        </Space>
                    </Tooltip>
                );
            },
        },
        {
            title: "地面处理代理",
            dataIndex: ["master", "gha"],
            key: "gha",
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (gha) => (
                <Tooltip placement="topLeft" title={gha || "未指定"}>
                    <span style={{ fontSize: '12px' }}>{gha || "未指定"}</span>
                </Tooltip>
            ),
        },
        {
            title: "所有者",
            dataIndex: ["master", "owner"],
            key: "owner",
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (owner) => (
                <Tooltip placement="topLeft" title={owner || "未指定"}>
                    <span style={{ fontSize: '12px' }}>{owner || "未指定"}</span>
                </Tooltip>
            ),
        },
        {
            title: "持有者",
            dataIndex: ["master", "holder"],
            key: "holder",
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (holder) => (
                <Tooltip placement="topLeft" title={holder || "未指定"}>
                    <span style={{ fontSize: '12px' }}>{holder || "未指定"}</span>
                </Tooltip>
            ),
        },
        {
            title: "标签",
            key: "tags",
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => {
                const tags = record.master?.tags || [];
                const displayTags = tags.slice(0, 2); // 只显示前2个标签
                const remainingCount = tags.length - 2;
                
                return (
                    <Space size={2} wrap>
                        {displayTags.map((tag, index) => (
                            <Tag 
                                key={index} 
                                size="small" 
                                color={tag.color_code || "#2db7f5"}
                                style={{ fontSize: '10px', margin: '1px' }}
                            >
                                {tag.tag_name}
                            </Tag>
                        ))}
                        {remainingCount > 0 && (
                            <Tag size="small" color="default" style={{ fontSize: '10px', margin: '1px' }}>
                                +{remainingCount}
                            </Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: "照片",
            key: "photos",
            width: 60,
            align: "center",
            render: (_, record) => {
                const count = (record.master?.photos || []).reduce(
                    (sum, ev) => sum + (ev.items?.length || 0), 0
                );
                return count > 0 ? (
                    <Tooltip title={`${count} 张照片 - 点击查看`}>
                        <span
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setPhotoRecord(record);
                                setPhotoModalVisible(true);
                            }}
                        >
                            <Badge count={count} size="small" offset={[4, -2]}>
                                <PictureOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                            </Badge>
                        </span>
                    </Tooltip>
                ) : null;
            },
        },
        {
            title: "备注",
            dataIndex: ["master", "remarks"],
            key: "remarks",
            width: 120,
            ellipsis: {
                showTitle: false,
            },
            render: (remarks) => (
                <Tooltip placement="topLeft" title={remarks || "无备注"}>
                    <span style={{ fontSize: '12px', color: remarks ? '#333' : '#999' }}>
                        {remarks || "-"}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: "创建时间",
            dataIndex: ["master", "created_at"],
            key: "created_at",
            sorter: true,
            sortOrder: sortInfo.sortBy === "created_at" && sortInfo.sortOrder ? 
                (sortInfo.sortOrder === "asc" ? "ascend" : "descend") : false,
            width: 150,
            ellipsis: {
                showTitle: false,
            },
            render: (created_at) => {
                if (!created_at) return '-';
                const date = new Date(created_at);
                const formattedTime = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
                
                return (
                    <Tooltip placement="topLeft" title={formattedTime}>
                        <span style={{ fontSize: '12px' }}>{formattedTime}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: "更新时间",
            dataIndex: ["master", "updated_at"],
            key: "updated_at",
            sorter: true,
            sortOrder: sortInfo.sortBy === "updated_at" && sortInfo.sortOrder ? 
                (sortInfo.sortOrder === "asc" ? "ascend" : "descend") : false,
            width: 150,
            ellipsis: {
                showTitle: false,
            },
            render: (updated_at) => {
                if (!updated_at) return '-';
                const date = new Date(updated_at);
                const formattedTime = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
                
                return (
                    <Tooltip placement="topLeft" title={formattedTime}>
                        <span style={{ fontSize: '12px' }}>{formattedTime}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: "租赁期限",
            key: "lease_period",
            width: 140,
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => {
                const ownershipType = record.master?.ownership_type;
                if (ownershipType !== '租赁') {
                    return <span style={{ color: '#999', fontSize: '12px' }}>非租赁</span>;
                }

                const leaseStart = record.master?.lease_start_date;
                const leaseEnd = record.master?.lease_end_date;
                
                if (!leaseStart) {
                    return <span style={{ color: '#ff4d4f', fontSize: '12px' }}>租赁日期缺失</span>;
                }

                const formatDate = (dateStr) => {
                    if (!dateStr) return '';
                    const date = new Date(dateStr);
                    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
                };

                const startDate = formatDate(leaseStart);
                const endDate = leaseEnd ? formatDate(leaseEnd) : '永久';
                const displayText = `${startDate} 至 ${endDate}`;

                return (
                    <Tooltip placement="topLeft" title={`租赁开始: ${startDate}${leaseEnd ? `\n租赁结束: ${formatDate(leaseEnd)}` : '\n租赁结束: 永久'}`}>
                        <span style={{ fontSize: '12px' }}>{displayText}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: "剩余租赁天数",
            key: "remaining_lease_days",
            width: 110,
            ellipsis: {
                showTitle: false,
            },
            render: (_, record) => {
                const ownershipType = record.master?.ownership_type;
                if (ownershipType !== '租赁') {
                    return <span style={{ color: '#999', fontSize: '12px' }}>-</span>;
                }

                const leaseEnd = record.master?.lease_end_date;
                
                // 如果没有结束日期，表示永久租赁
                if (!leaseEnd) {
                    return (
                        <Tooltip placement="topLeft" title="永久租赁，无到期日">
                            <span style={{ color: '#52c41a', fontSize: '12px', fontWeight: '500' }}>
                                永久
                            </span>
                        </Tooltip>
                    );
                }

                // 计算剩余天数
                const today = new Date();
                const endDate = new Date(leaseEnd);
                const timeDiff = endDate.getTime() - today.getTime();
                const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

                let displayText, color, tooltipText;
                
                if (daysDiff > 30) {
                    // 超过30天：绿色，正常
                    displayText = `${daysDiff}天`;
                    color = '#52c41a';
                    tooltipText = `还有${daysDiff}天到期`;
                } else if (daysDiff > 7) {
                    // 7-30天：橙色，提醒
                    displayText = `${daysDiff}天`;
                    color = '#fa8c16';
                    tooltipText = `即将到期，还有${daysDiff}天`;
                } else if (daysDiff >= 0) {
                    // 0-7天：红色，紧急
                    displayText = `${daysDiff}天`;
                    color = '#ff4d4f';
                    tooltipText = `即将到期，仅剩${daysDiff}天`;
                } else {
                    // 已过期：深红色
                    displayText = `已过期${Math.abs(daysDiff)}天`;
                    color = '#a8071a';
                    tooltipText = `租赁已过期${Math.abs(daysDiff)}天`;
                }

                return (
                    <Tooltip placement="topLeft" title={`${tooltipText}\n到期日期: ${endDate.getFullYear()}/${String(endDate.getMonth() + 1).padStart(2, '0')}/${String(endDate.getDate()).padStart(2, '0')}`}>
                        <span style={{ 
                            color: color, 
                            fontSize: '12px', 
                            fontWeight: '500',
                            ...(daysDiff < 0 && { 
                                backgroundColor: '#fff2f0', 
                                padding: '2px 4px', 
                                borderRadius: '4px',
                                border: '1px solid #ffccc7'
                            })
                        }}>
                            {displayText}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "最后状态变更原因",
            dataIndex: "last_status_change_reason",
            key: "last_status_change_reason",
            width: 140,
            ellipsis: {
                showTitle: false,
            },
            render: (reason) => (
                <Tooltip placement="topLeft" title={reason || "无"}>
                    <span style={{ fontSize: '12px', color: reason ? '#333' : '#999' }}>
                        {reason || "-"}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: "操作",
            key: "action",
            width: 80,
            fixed: 'right',
            render: (_, record) => (
                <UldActionDropdownV3 
                    record={record}
                    onRefresh={fetchUlds}
                    onEdit={handleEditUld}
                    onViewDetail={handleViewDetail}
                    onViewHistory={handleViewHistory}
                    onManageTags={handleManageTags}
                    size="small"
                />
            ),
        },
    ];

    // 处理表格分页、排序变化
    const handleTableChange = (pagination, filters, sorter) => {
        updatePagination(pagination.current, pagination.pageSize);

        // 更新排序信息 - 只允许创建时间和更新时间排序
        if (sorter.field && sorter.order) {
            let fieldName;
            if (Array.isArray(sorter.field)) {
                fieldName = sorter.field[sorter.field.length - 1]; // 取数组最后一个元素
            } else {
                fieldName = sorter.field;
            }
            
            const allowedSortFields = ['created_at', 'updated_at'];
            if (allowedSortFields.includes(fieldName)) {
                const newSortInfo = {
                    sortBy: fieldName,
                    sortOrder: sorter.order === "ascend" ? "asc" : "desc",
                };
                updateSortInfo(newSortInfo);
            } else {
                updateSortInfo({
                    sortBy: null,
                    sortOrder: null,
                });
            }
        } else {
            updateSortInfo({
                sortBy: null,
                sortOrder: null,
            });
        }
    };

    // 处理编辑ULD
    const handleEditUld = (record) => {
        // 传递ULD的master信息用于编辑
        showEditForm(record.master);
    };

    // 处理查看详情
    const handleViewDetail = () => {};

    // 处理查看历史
    const handleViewHistory = (record) => {
        // 跳转到历史记录页面，并预设ULD筛选条件
        const uldIdentifier = record.master.uld_identifier;
        const searchParams = new URLSearchParams({
            uld_identifier: uldIdentifier,
            auto_search: 'true'  // 标记自动执行搜索
        });
        
        navigate(`/uld-history?${searchParams.toString()}`);
    };

    // 处理管理标签
    const handleManageTags = () => {};

    return (
        <ConfigProvider
            theme={{
                components: {
                    Table: {
                        cellPaddingBlock: 6, // 减少垂直内边距 (默认16px -> 6px)
                        cellPaddingInline: 8, // 减少水平内边距 (默认16px -> 8px)
                        headerBg: '#fafafa', // 表头背景色
                        headerColor: '#262626', // 表头文字颜色
                        rowHoverBg: '#f5f5f5', // 行悬停背景色
                        borderColor: '#f0f0f0', // 边框颜色
                        fontSize: 13, // 字体大小
                        lineHeight: 1.4, // 行高
                    },
                },
            }}
        >
            <div className="uld-table-container">
                <Table
                    ref={tableRef}
                    columns={columns}
                    dataSource={uldList}
                    rowKey={(record) => record.master.id}
                    loading={loading}
                    rowSelection={{
                        ...rowSelection,
                        columnWidth: 6, // 明确设置选择框列宽
                    }}
                    size="small"
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: totalUlds,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
                        pageSizeOptions: ['30', '50', '100', '200'],
                        defaultPageSize: 50,
                        size: 'small',
                    }}
                    onChange={handleTableChange}
                    scroll={{ 
                        x: 1660, // 调整水平滚动最小宽度，新增最后状态变更原因列
                        y: 'calc(100vh - 320px)', // 垂直滚动高度，优化计算方式
                        scrollToFirstRowOnChange: false // 切换页面时不自动滚动到顶部
                    }}
                    virtual // 启用虚拟滚动，提升大数据量性能
                    onRow={getCustomRowProps}
                    sticky={{
                        offsetHeader: 0, // 粘性表头
                    }}
                />
            </div>

            <style jsx>{`
                /* ULD表格容器优化样式 */
                .uld-table-container {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                
                .uld-table-container .ant-table-wrapper {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }
                
                .uld-table-container .ant-table {
                    flex: 1;
                }
                
                .uld-table-container .ant-table-container {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                }
                
                .uld-table-container .ant-table-body {
                    flex: 1;
                    overflow: auto !important;
                }

                /* 确保表格内容可以被选择 */
                .uld-table-container .ant-table-tbody > tr > td {
                    user-select: text !important;
                    -webkit-user-select: text !important;
                    -moz-user-select: text !important;
                    -ms-user-select: text !important;
                }

                /* 只在序号列阻止文本选择 */
                .uld-table-container .ant-table-tbody > tr > td:first-child {
                    user-select: none !important;
                    -webkit-user-select: none !important;
                    -moz-user-select: none !important;
                    -ms-user-select: none !important;
                }
                
                /* 表格行选择样式优化 */
                .uld-table-container .ant-table-tbody > tr.row-selected > td {
                    background-color: #e6f7ff !important;
                }
                
                /* 表格行悬停效果 */
                .uld-table-container .ant-table-tbody > tr:hover > td {
                    background-color: #f5f5f5 !important;
                }
                
                .uld-table-container .ant-table-tbody > tr.row-selected:hover > td {
                    background-color: #d4edff !important;
                }
                
                /* 固定列阴影优化 */
                .uld-table-container .ant-table-cell-fix-left,
                .uld-table-container .ant-table-cell-fix-right {
                    background: #fff !important;
                }
                
                .uld-table-container .ant-table-cell-fix-left-last::after {
                    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.06) !important;
                }
                
                .uld-table-container .ant-table-cell-fix-right-first::after {
                    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.06) !important;
                }
                
                /* 表格滚动条优化 */
                .uld-table-container .ant-table-body::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                
                .uld-table-container .ant-table-body::-webkit-scrollbar-thumb {
                    background-color: rgba(0, 0, 0, 0.2);
                    border-radius: 4px;
                }
                
                .uld-table-container .ant-table-body::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(0, 0, 0, 0.3);
                }
                
                .uld-table-container .ant-table-body::-webkit-scrollbar-track {
                    background-color: rgba(0, 0, 0, 0.05);
                }
                
                /* 紧凑型表格样式 */
                .uld-table-container .ant-table-small .ant-table-thead > tr > th {
                    font-weight: 600;
                    text-align: center;
                    background: #fafafa;
                    border-bottom: 2px solid #f0f0f0;
                    height: 36px;
                    padding: 8px 6px !important;
                }
                
                .uld-table-container .ant-table-small .ant-table-tbody > tr > td {
                    text-align: center;
                    height: 32px;
                    padding: 6px !important;
                    vertical-align: middle;
                }
                
                /* 序号列特殊样式 */
                .uld-table-container .ant-table-tbody > tr > td:first-child {
                    padding: 2px !important;
                }
                
                /* 标签样式优化 */
                .uld-table-container .ant-tag {
                    border-radius: 2px;
                    font-size: 11px;
                    line-height: 16px;
                    padding: 0 4px;
                    margin: 1px;
                    height: 18px;
                    display: inline-flex;
                    align-items: center;
                }
                
                /* 操作列居中 */
                .uld-table-container .ant-table-tbody > tr > td:last-child {
                    text-align: center !important;
                    padding: 2px !important;
                }
                
                /* ULD识别码列样式 */
                .uld-table-container .ant-table-tbody > tr > td:nth-child(2) {
                    font-weight: 500;
                    color: #1890ff;
                }
                
                /* 表格分页优化 */
                .uld-table-container .ant-table-pagination {
                    margin: 8px 0 0 0 !important;
                    flex-shrink: 0;
                }
                
                /* 响应式优化 */
                @media (max-width: 1400px) {
                    .uld-table-container .ant-table-small .ant-table-thead > tr > th,
                    .uld-table-container .ant-table-small .ant-table-tbody > tr > td {
                        padding: 4px !important;
                        font-size: 11px;
                    }
                    
                    .uld-table-container .ant-tag {
                        font-size: 10px;
                        padding: 0 3px;
                        height: 16px;
                    }
                }
                
                @media (max-width: 1200px) {
                    .uld-table-container .ant-table-small .ant-table-thead > tr > th,
                    .uld-table-container .ant-table-small .ant-table-tbody > tr > td {
                        padding: 3px !important;
                        font-size: 10px;
                    }
                    
                    .uld-table-container .ant-table-small .ant-table-tbody > tr > td {
                        height: 28px;
                    }
                }
            `}</style>

            <UldPhotoModal
                visible={photoModalVisible}
                onCancel={() => setPhotoModalVisible(false)}
                onSuccess={() => {
                    setPhotoModalVisible(false);
                    fetchUlds();
                }}
                record={photoRecord}
            />
        </ConfigProvider>
    );
};

export default UldTable;
